import { inject, Injectable } from "@angular/core";
import { CampaignType, SPStrategy, SpStrategyCreateRequest, StrategyType } from "@front/m19-api-client";
import { SpStrategyEx, StrategyGroupEx } from "@front/m19-models";
import { AudiencesService, Constant, SpStrategyService } from "@front/m19-services";
import { combineLatest, map, Observable, switchMap, take } from "rxjs";
import { BulkUploadBuckets, BulkUploadRowPreview } from "../bulk-upload-context.service";
import { BulkFieldValidator, StrategyBulkUploadHandler } from "../strategy/strategy-bulk-upload-handler.service";
import { BulkUploadPreviewStatus } from "@m19-board/bulk-upload/bulk-upload-grid-cells/bulk-upload-type-icon.component";

interface StrategyGroupProjection {
  productStrategies: Map<number, { strategyId: string; strategyType: StrategyType; asins: string[] }>;
  brandStrategies: Map<number, { strategyId: string; strategyType: StrategyType; asins: string[] }>;
  keywordStrategies: Map<number, { strategyId: string; strategyType: StrategyType; asins: string[] }>;
}

@Injectable()
export class SpBulkUploadHandler extends StrategyBulkUploadHandler<SPStrategy> {
  private readonly spStrategyService = inject(SpStrategyService);
  private readonly audiencesService = inject(AudiencesService);

  protected readonly campaignType = CampaignType.SP;
  protected readonly bulkConfig = this.bulkImportService.getBulkConfig(CampaignType.SP);

  protected readonly audiences$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.audiencesService.getAudiences(am.accountId, am.marketplace, CampaignType.SP)),
  );

  protected readonly strategyGroupIndex$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.spStrategyService.getStrategyGroups(am.accountId, am.marketplace)),
  );

  protected readonly spStrategyIndex$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.spStrategyService.getStrategies(am.accountId, am.marketplace)),
  );

  override buildPreview(rows: string[]): Observable<BulkUploadBuckets<SPStrategy>> {
    return super.buildPreview(rows).pipe(
      switchMap((preview) =>
        combineLatest([this.strategyGroupIndex$.pipe(take(1)), this.spStrategyIndex$.pipe(take(1))]).pipe(
          map(([strategyGroupIndex, strategyIndex]) => {
            const projected = this.projectStrategyGroupAsins(preview, strategyGroupIndex);
            projected.forEach((groupProjection, groupId) => {
              this.checkStrategyGroupAsinsConstraints(groupProjection, preview, groupId, strategyIndex);
            });
            return preview;
          }),
        ),
      ),
    );
  }

  protected buildCreateRequest$(creation: BulkUploadRowPreview<SPStrategy>): Observable<SPStrategy> {
    const parsed = creation.parsedEntity;
    const body: SpStrategyCreateRequest = {
      name: parsed.name!,
      asins: parsed.asins?.map((a) => a.asin) ?? [],
      algoMode: parsed.algoMode!,
      strategyType: parsed.strategyType,
      strategyGroupId: parsed.strategyGroupId,
      monthlyBudget: parsed.monthlyBudget,
      acosTarget: parsed.acosTarget,
      dailyBudget: parsed.dailyBudget,
      suggestedBid: parsed.suggestedBid,
      disableOtherQueries: parsed.disableOtherQueries,
      state: parsed.state,
      asinIsolation: parsed.asinIsolation,
      priority: parsed.priority,
      targetings: parsed.targetings,
    };

    if (!parsed.strategyGroupId) {
      return this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
        take(1),
        switchMap((am) =>
          this.spStrategyService.createStrategyGroup(
            {
              accountId: am.accountId,
              marketplace: am.marketplace,
              strategyGroupName: body.name,
            },
            body,
          ),
        ),
        map((strategyGroup) => strategyGroup.productStrategies[0]),
      );
    }

    return this.spStrategyService.createStrategy(body);
  }

  protected extraUpdateParams(parsedEntity: Partial<SPStrategy>) {
    return {
      boostAudienceId: parsedEntity.boostAudienceId ?? undefined,
    };
  }

  protected getFieldValidators(): Partial<Record<keyof SPStrategy, BulkFieldValidator<SPStrategy>>> {
    return {
      strategyId: (entity) => this.validateStrategyIdNumber(entity),
      name: (entity) => this.validateName(entity),
      state: (entity) => this.validateState(entity),
      asins: (entity) => this.validateAsins(entity),
      boostAudienceId: (entity) => this.validateBoostAudienceId(entity),
    };
  }

  protected getCreationFieldValidators(): Partial<Record<keyof SPStrategy, BulkFieldValidator<SPStrategy>>> {
    return {
      strategyType: (entity) => this.validateStrategyTypeOnCreate(entity),
    };
  }

  protected getUpdateFieldValidators(): Partial<Record<keyof SPStrategy, BulkFieldValidator<SPStrategy>>> {
    return {
      strategyId: (entity) => this.validateStrategyExists(entity),
      state: (entity) => this.validateStateOnUpdate(entity),
      name: (entity) => this.validateNameOnUpdate(entity),
    };
  }

  private validateBoostAudienceId(entity: Partial<SPStrategy>): Observable<string | null> | null {
    if (entity.boostAudienceId === undefined) return null;

    return this.audiences$.pipe(
      take(1),
      map((audiences) => {
        if (audiences.some((audience) => audience.audienceId === entity.boostAudienceId)) return null;
        return "Boost audience ID is not valid";
      }),
    );
  }

  private validateStrategyTypeOnCreate(entity: Partial<SPStrategy>): string | Observable<string | null> | null {
    const strategyType = entity.strategyType;
    if (strategyType === StrategyType.LEGACY) {
      return "Creation of legacy strategies is no longer allowed. Create a PRODUCT strategy instead";
    }
    if (strategyType === StrategyType.PRODUCT) {
      if (!entity.strategyGroupId) return null;
      return this.strategyGroupIndex$.pipe(
        take(1),
        map((strategyGroupIndex) => {
          const strategyGroup = strategyGroupIndex.get(entity.strategyGroupId!);
          if (!strategyGroup) return "Strategy group does not exist for the provided id";
          return null;
        }),
      );
    }

    if (entity.strategyGroupId === 0) {
      return "Strategy group should be > 0 for BRAND (brand defense) or KEYWORD (focus) strategies";
    }
    return this.strategyGroupIndex$.pipe(
      take(1),
      map((strategyGroupIndex) => {
        const strategyGroup = strategyGroupIndex.get(entity.strategyGroupId!);
        if (!strategyGroup) return "Strategy group does not exist for the provided id";

        if (strategyType === StrategyType.BRAND) {
          const brandDefenseStrategies = strategyGroup.strategies!.filter((s) => s.strategyType === StrategyType.BRAND);
          if (brandDefenseStrategies.length >= Constant.maxBrandDefenseStrategiesByStrategyGroup) {
            return `The same strategy group cannot have more than ${Constant.maxBrandDefenseStrategiesByStrategyGroup} brand defense strategies`;
          }
        } else if (strategyType === StrategyType.KEYWORD) {
          const keywordStrategies = strategyGroup.strategies!.filter((s) => s.strategyType === StrategyType.KEYWORD);
          if (keywordStrategies.length >= Constant.maxKeywordStrategiesByStrategyGroup) {
            return `The same strategy group cannot have more than ${Constant.maxKeywordStrategiesByStrategyGroup} focus strategies`;
          }
        }
        return null;
      }),
    );
  }

  private projectStrategyGroupAsins(
    rows: BulkUploadRowPreview<SPStrategy>[],
    strategyGroupIndex: Map<number, StrategyGroupEx>,
  ): Map<number, StrategyGroupProjection> {
    const strategyGroupsProjection = new Map<number, StrategyGroupProjection>();

    rows.forEach((row, index) => {
      if (row.status !== BulkUploadPreviewStatus.CREATION && row.status !== BulkUploadPreviewStatus.UPDATE) return;
      const parsed = row.parsedEntity;
      const strategyGroup = parsed.strategyGroupId ? strategyGroupIndex.get(parsed.strategyGroupId) : null;
      if (!strategyGroup || !parsed.asins || !parsed.strategyGroupId) return;

      if (!strategyGroupsProjection.has(parsed.strategyGroupId)) {
        strategyGroupsProjection.set(parsed.strategyGroupId, this.initStrategyGroupProjection(strategyGroup));
      }

      const projectionId = row.status === BulkUploadPreviewStatus.CREATION ? -index - 1 : parsed.strategyId!;
      const strategyProjection: { strategyId: string; strategyType: StrategyType; asins: string[] } = {
        strategyId: String(projectionId),
        strategyType: parsed.strategyType!,
        asins: parsed.asins.map((a) => a.asin),
      };

      if (parsed.strategyType === StrategyType.PRODUCT) {
        strategyGroupsProjection.get(parsed.strategyGroupId)!.productStrategies.set(projectionId, strategyProjection);
      } else if (parsed.strategyType === StrategyType.BRAND) {
        strategyGroupsProjection.get(parsed.strategyGroupId)!.brandStrategies.set(projectionId, strategyProjection);
      } else if (parsed.strategyType === StrategyType.KEYWORD) {
        strategyGroupsProjection.get(parsed.strategyGroupId)!.keywordStrategies.set(projectionId, strategyProjection);
      }
    });

    return strategyGroupsProjection;
  }

  private initStrategyGroupProjection(strategyGroup: StrategyGroupEx): StrategyGroupProjection {
    return {
      productStrategies: new Map(
        strategyGroup.productStrategies.map((s) => [+s.strategyId, this.toStrategyProjection(s)]),
      ),
      brandStrategies: new Map(strategyGroup.brandStrategies.map((s) => [+s.strategyId, this.toStrategyProjection(s)])),
      keywordStrategies: new Map(
        strategyGroup.keywordStrategies.map((s) => [+s.strategyId, this.toStrategyProjection(s)]),
      ),
    };
  }

  private toStrategyProjection(strategy: SpStrategyEx): {
    strategyId: string;
    strategyType: StrategyType;
    asins: string[];
  } {
    return {
      strategyId: String(strategy.strategyId),
      strategyType: strategy.strategyType,
      asins: strategy.asins.map((a) => a.asin),
    };
  }

  private checkStrategyGroupAsinsConstraints(
    strategyGroupProjection: StrategyGroupProjection,
    rows: BulkUploadRowPreview<SPStrategy>[],
    groupId: number,
    strategyIndex: Map<number, SPStrategy>,
  ): void {
    const productAsins = new Set<string>();
    const asinCount = new Map<string, number>();
    strategyGroupProjection.productStrategies.forEach((strategy) => {
      for (const asin of strategy.asins) {
        productAsins.add(asin);
        asinCount.set(asin, (asinCount.get(asin) ?? 0) + 1);
      }
    });

    const duplicatedAsins = new Set([...asinCount.entries()].filter(([, count]) => count > 1).map(([asin]) => asin));

    const brandKwAsins = new Set<string>();
    strategyGroupProjection.brandStrategies.forEach((strategy) => {
      for (const asin of strategy.asins) brandKwAsins.add(asin);
    });
    strategyGroupProjection.keywordStrategies.forEach((strategy) => {
      for (const asin of strategy.asins) brandKwAsins.add(asin);
    });
    const orphans = new Set([...brandKwAsins].filter((asin) => !productAsins.has(asin)));

    for (const row of rows) {
      if (row.parsedEntity.strategyGroupId !== groupId) continue;
      const parsedAsins = row.parsedEntity.asins;
      if (!parsedAsins) continue;

      if (row.parsedEntity.strategyType === StrategyType.PRODUCT) {
        if (parsedAsins.some((a) => duplicatedAsins.has(a.asin))) {
          this.setAsinsError(row, "Some ASINs are already in the strategy group");
          continue;
        }
        const live = strategyIndex.get(row.parsedEntity.strategyId!);
        if (live?.asins.some((a) => !parsedAsins.some((p) => p.asin === a.asin) && orphans.has(a.asin))) {
          this.setAsinsError(row, "Some ASINs are used in brand or keyword strategies");
        }
        continue;
      }

      if (
        (row.parsedEntity.strategyType === StrategyType.BRAND ||
          row.parsedEntity.strategyType === StrategyType.KEYWORD) &&
        parsedAsins.some((a) => orphans.has(a.asin))
      ) {
        this.setAsinsError(row, "Some ASINs are not in the strategy group");
      }
    }
  }

  private setAsinsError(row: BulkUploadRowPreview<SPStrategy>, error: string): void {
    const asinsField = row.fieldsPreview.find((field) => field.field === "asins");
    if (!asinsField || asinsField.error) return;
    asinsField.error = error;
    row.status = BulkUploadPreviewStatus.VALUE_ERROR;
  }
}
